import { DataIndex } from 'Util/DataIndexUtils';
import { IRequirementEntityAttributes } from 'Models/Entities';
import { uniqBy } from 'lodash';

/**
 * Search an indexed requirement tree for one of a set of target
 * parent requirements, from a specified requirement.
 *
 * @param requirementId requirement to search upwards from
 * @param targetParentRequirements requirement ids of potential parent requirements
 * @param allRequirementsById all requirements in tree
 * @returns requirement attributes for found parent requirement
 */
export const findParentRequirement = (
	requirementId: string,
	targetParentRequirements: string[],
	allRequirementsById: DataIndex<IRequirementEntityAttributes>,
) => {
	const traverseUpwards = (currentRequirementId: string): IRequirementEntityAttributes => {
		const requirement = allRequirementsById[currentRequirementId];

		const isTargetRequirement = targetParentRequirements.includes(currentRequirementId);
		const hasParentRequirement = !requirement?.parentRequirementId;

		if (!isTargetRequirement && hasParentRequirement) {
			// if we hit this point we've reached the top of the
			// requirement tree without finding a requirement
			// assigned to the plan
			// eslint-disable-next-line max-len
			throw new Error(`Requirement not assigned to plan but does not have a parent requirement ${currentRequirementId}`);
		}

		return isTargetRequirement
			? requirement
			: traverseUpwards(requirement.parentRequirementId!);
	};

	return traverseUpwards(requirementId);
};

/**
 * 2nd Level Requirement are always assigned to a plan, however
 * the conditions of their parent requirement determines how the user
 * select them and how they are presented.
 *
 * @param requirements
 * @param allRequirementsById
 * @returns
 */
export const swapForPresentationRequirements = (
	requirements: IRequirementEntityAttributes[],
	allRequirementsById: DataIndex<IRequirementEntityAttributes>,
) => {
	const displayRequirements = requirements
		.map(requirement => {
			if (!requirement.parentRequirementId) {
				return requirement;
			}

			/*
			* allRequirementsById contains a dictionary of RequirementEntities
			* A requirement has a parentRequirementId that points at its parent requirement node
			* Using a while loop to find the child of the top level requirement
			*/
			let target: IRequirementEntityAttributes = requirement;
			while (target.parentRequirementId) {
				const newTarget = allRequirementsById[target.parentRequirementId];

				// stop the loop when the new requirement is not found, or the requirement has condition = OR
				if (!newTarget || newTarget.condition === 'OR') {
					break;
				}

				target = newTarget;
			}

			const parent: IRequirementEntityAttributes | null = allRequirementsById[target.id ?? ''];

			if (parent && parent.condition === 'AND') {
				return parent;
			}

			return requirement;
		});

	return displayRequirements;
};

export const getRequirementComponents = (
	requirementIds: string[],
	requirementsById: DataIndex<IRequirementEntityAttributes>,
): IRequirementEntityAttributes[] => {
	const parentRequirements = requirementIds
		.map(requirementId => findParentRequirement(requirementId, requirementIds, requirementsById))
		.filter(requirement => !!requirement);
	const distinctRequirements = uniqBy(
		parentRequirements,
		requirement => requirement.id,
	);

	return swapForPresentationRequirements(distinctRequirements, requirementsById);
};
